/* eslint-disable camelcase */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  H2,
  H4,
  P,
  Modal,
  ModalHeader,
  ModalBody,
  Icon,
  Grid,
  Header,
  FlexBaseCol,
  FlexRow,
  BaseCol,
  ProgressBar
} from '@contently/toolkit';
import styled from '@emotion/styled';
import CalculationBox from './calculation_box';
import FilterWrapper from './filter_wrapper';
import MonthlyChartWrapper from './monthly_chart_wrapper';
import filterHelper from './filterHelper';
import {
  removeCentsAddCommas,
  percIncrease,
  generateLink,
  validateStoredRegion,
  addTooltipToMonthlyData,
  US_AND_CANADA
} from './utils';
import FilterContext from './FilterContext';
import TableWrapper from './table_wrapper';
import { tagColumns, pillarColumns, audienceColumns } from './tableConfig';

const GrayBorderDiv = styled('div')`
  border: 1px solid #d4d4d4;
  padding: 15px 20px;
  margin: 0 18px 0 0;
  height: 100%;
  @media screen and (max-width: 768px) {
    margin-right: 0;
  }
`;

const BaseColSpace = styled(BaseCol)`
  margin-bottom: 20px;
`;

const DarkGrayBorderDiv = styled(GrayBorderDiv)`
  border: 1px solid #999;
`;

const ProgressDiv = styled(GrayBorderDiv)`
  border-color: #efefef;
  background-color: #fcfcfc;
`;

const SectionTitle = styled('div')`
  margin: 20px 0 14px 0;
`;

const DashboardText = styled('div')`
  font-size: 12px;
  margin: auto 18px auto auto;
  text-align: right;
  width: 50%;
`;

const LargeParagraphText = styled(P)`
  margin-top: 10px;
  font-size: 16px;
`;

const CenteredFlexRow = styled(FlexRow)`
  align-items: center;
  margin-bottom: 1rem;
`;

const GridWithBottomMargin = styled(Grid)`
  @media screen and (max-width: 768px) {
    margin-bottom: 6rem;
  }
`;

const FlexRowWithTopMargin = styled(FlexRow)`
  margin-top: 2rem;
`;

function ValueWrapper({ appData }) {
  const {
    pub_info: pubInfo,
    value_info: valueInfo,
    chart_info: chartInfo,
    tag_info: tagInfo,
    pillar_info: pillarInfo,
    audience_info: audienceInfo,
    month_over_month_difference: toolTipData
  } = JSON.parse(appData);

  const [modal, setModal] = useState(false);

  const storedRegion = localStorage.getItem('contentValueRegion');
  validateStoredRegion(storedRegion);
  const [filterState, setFilterState] = useState(storedRegion || US_AND_CANADA);

  const chartPubInfo = { pubId: pubInfo.pub_id, pubName: pubInfo.pub_name };

  const {
    total_organic_search_traffic_benchmark: totalOrganicSearchTrafficBenchmark,
    total_organic_search_traffic_value_benchmark: totalOrganicSearchTrafficValueBenchmark,
    total_content_debit_benchmark: totalContentDebitBenchmark,
    last_month_name: lastFullMonthName
  } = valueInfo;

  const {
    total_organic_search_traffic: totalOrganicSearchTraffic,
    total_organic_search_traffic_last_month: totalOrganicSearchTrafficLastMonth,
    total_organic_search_traffic_value: totalOrganicSearchTrafficValue,
    total_organic_search_traffic_value_last_month: totalOrganicSearchTrafficValueLastMonth,
    total_content_debit: totalContentDebit,
    total_content_debit_last_month: totalContentDebitLastMonth,
    average_cpc: averageCpc,
    average_cpc_last_month: averageCpcLastMonth,
    monthlyData
  } = filterHelper(valueInfo, chartInfo, filterState);
  const monthlyDataWithTooltip = toolTipData
    ? addTooltipToMonthlyData(monthlyData, toolTipData)
    : monthlyData;

  const { pub_id: pubId, completed_pubs: completedPubs, published_pubs: publishedPubs } = pubInfo;
  const queryParams = { hide_filters: false, publications: pubId, content_value: 'none' };

  const percentPublished = (publishedPubs / completedPubs) * 100;

  const netSeoTrafficValue = totalOrganicSearchTrafficValue - totalContentDebit;
  const netSeoTrafficValueLastMonth =
    totalOrganicSearchTrafficValueLastMonth - totalContentDebitLastMonth;
  const netSeoTrafficValueBenchmark =
    totalOrganicSearchTrafficValueBenchmark - totalContentDebitBenchmark;

  return (
    <Fragment>
      <Modal isOpen={modal} onClose={() => setModal(false)}>
        <ModalHeader>
          <H2>Content Value</H2>
        </ModalHeader>
        <ModalBody>
          <P>
            This dashboard measures how much organic search traffic your published content has
            generated to date, and estimates the monetary value of that traffic in the Adwords
            marketplace. To learn more about how we calculate Content Value, visit{' '}
            <a href="https://contently-support.zendesk.com/hc/en-us/articles/21627490043154-Understanding-and-improving-your-Content-Value-dashboard">
              this article
            </a>{' '}
            in Contently&apos;s help center.
          </P>
        </ModalBody>
      </Modal>
      <GridWithBottomMargin>
        <CenteredFlexRow>
          <BaseCol md={5}>
            <Header>
              <H2>
                Content Value
                <Icon type="question-circle" onClick={() => setModal(true)} />
              </H2>
            </Header>
          </BaseCol>
          <BaseCol md={5}>
            <DashboardText>Dashboard updates automatically at the end of each month</DashboardText>
          </BaseCol>
          <FlexBaseCol>
            <FilterWrapper filterState={filterState} setFilterState={setFilterState} />
          </FlexBaseCol>
        </CenteredFlexRow>
        <FilterContext.Provider value={filterState}>
          <FlexRow>
            <BaseColSpace>
              <GrayBorderDiv>
                <MonthlyChartWrapper data={monthlyDataWithTooltip} pubInfo={chartPubInfo} />
              </GrayBorderDiv>
            </BaseColSpace>
          </FlexRow>
          <FlexRow>
            <BaseColSpace md={6}>
              <DarkGrayBorderDiv>
                <CalculationBox
                  id="total-organic-search-roi"
                  title="SEO Traffic Value"
                  value={`$${removeCentsAddCommas(totalOrganicSearchTrafficValue)}`}
                  subtext="The total cost to acquire your organic search traffic and keyword rankings with Google Ads."
                  previousValue={`$${removeCentsAddCommas(
                    totalOrganicSearchTrafficValueLastMonth
                  )}`}
                  lastFullMonthName={lastFullMonthName}
                  valuePercentage={percIncrease(
                    totalOrganicSearchTrafficValue,
                    totalOrganicSearchTrafficValueLastMonth
                  )}
                  valueChangeDirection={
                    totalOrganicSearchTrafficValue >= totalOrganicSearchTrafficValueLastMonth
                  }
                  benchmarkValue={`$${removeCentsAddCommas(
                    totalOrganicSearchTrafficValueBenchmark
                  )}`}
                  benchmarkPercentage={percIncrease(
                    totalOrganicSearchTrafficValue,
                    totalOrganicSearchTrafficValueBenchmark
                  )}
                  positive={
                    totalOrganicSearchTrafficValue >= totalOrganicSearchTrafficValueBenchmark
                  }
                />
              </DarkGrayBorderDiv>
            </BaseColSpace>
            <BaseColSpace md={6}>
              <ProgressDiv>
                <H4>Improve your content value</H4>
                <ProgressBar percent={percentPublished} />
                <LargeParagraphText>
                  {publishedPubs.toLocaleString()} published stories are included in the
                  calculations below, but you have {completedPubs.toLocaleString()} stories
                  completed. Mark{' '}
                  <a className="roi-link" href={generateLink(queryParams, 'stories/library?')}>
                    these stories
                  </a>{' '}
                  published (and add URLs) to ensure they&apos;re included next month
                </LargeParagraphText>
              </ProgressDiv>
            </BaseColSpace>
          </FlexRow>
          <FlexRow>
            <BaseColSpace md={6}>
              <GrayBorderDiv>
                <CalculationBox
                  id="average-cost-per-click"
                  title="AVERAGE CPC"
                  value={`$${averageCpc.toFixed(2).toLocaleString()}`}
                  previousValue={`$${averageCpcLastMonth.toFixed(2).toLocaleString()}`}
                  lastFullMonthName={lastFullMonthName}
                  valuePercentage={percIncrease(averageCpc, averageCpcLastMonth)}
                  valueChangeDirection={averageCpc >= averageCpcLastMonth}
                  subtext="The average cost-per-click associated with keywords that your content ranks for
                  organically."
                />
              </GrayBorderDiv>
            </BaseColSpace>
            <BaseColSpace md={6}>
              <GrayBorderDiv>
                <CalculationBox
                  id="total-traffic"
                  title="ORGANIC SEARCH TRAFFIC"
                  value={totalOrganicSearchTraffic.toLocaleString()}
                  subtext="The number of visitors who accessed your content via Google’s search engine
                  results pages."
                  previousValue={totalOrganicSearchTrafficLastMonth.toLocaleString()}
                  lastFullMonthName={lastFullMonthName}
                  valuePercentage={percIncrease(
                    totalOrganicSearchTraffic,
                    totalOrganicSearchTrafficLastMonth
                  )}
                  valueChangeDirection={
                    totalOrganicSearchTraffic >= totalOrganicSearchTrafficLastMonth
                  }
                  benchmarkValue={totalOrganicSearchTrafficBenchmark.toLocaleString()}
                  benchmarkPercentage={percIncrease(
                    totalOrganicSearchTraffic,
                    totalOrganicSearchTrafficBenchmark
                  )}
                  positive={totalOrganicSearchTraffic >= totalOrganicSearchTrafficBenchmark}
                />
              </GrayBorderDiv>
            </BaseColSpace>
          </FlexRow>
          <FlexRow>
            <BaseCol>
              <SectionTitle>
                <H4>Net Value</H4>
              </SectionTitle>
            </BaseCol>
          </FlexRow>
          <FlexRow>
            <BaseColSpace md={6}>
              <GrayBorderDiv>
                <CalculationBox
                  id="total-content-spend"
                  title="ASSOC. CONTENT SPEND"
                  value={`$${removeCentsAddCommas(totalContentDebit)}`}
                  subtext="The total amount spent on freelancer payments for all stories included in the
                  calculation."
                  previousValue={`$${removeCentsAddCommas(totalContentDebitLastMonth)}`}
                  lastFullMonthName={lastFullMonthName}
                  valuePercentage={percIncrease(totalContentDebit, totalContentDebitLastMonth)}
                  valueChangeDirection={totalContentDebit >= totalContentDebitLastMonth}
                  benchmarkValue={`$${removeCentsAddCommas(totalContentDebitBenchmark)}`}
                  benchmarkPercentage={percIncrease(totalContentDebit, totalContentDebitBenchmark)}
                  positive={totalContentDebit >= totalContentDebitBenchmark}
                />
              </GrayBorderDiv>
            </BaseColSpace>
            <BaseColSpace md={6}>
              <GrayBorderDiv>
                <CalculationBox
                  id="total-traffic-value"
                  title="NET SEO TRAFFIC VALUE"
                  value={`$${removeCentsAddCommas(netSeoTrafficValue)}`}
                  subtext="The total traffic value remaining after subtracting content spend from associated
                  stories."
                  previousValue={`$${removeCentsAddCommas(netSeoTrafficValueLastMonth)}`}
                  lastFullMonthName={lastFullMonthName}
                  valuePercentage={percIncrease(netSeoTrafficValue, netSeoTrafficValueLastMonth)}
                  valueChangeDirection={netSeoTrafficValue >= netSeoTrafficValueLastMonth}
                  benchmarkValue={`$${removeCentsAddCommas(netSeoTrafficValueBenchmark)}`}
                  benchmarkPercentage={percIncrease(
                    netSeoTrafficValue,
                    netSeoTrafficValueBenchmark
                  )}
                  positive={netSeoTrafficValue >= netSeoTrafficValueBenchmark}
                />
              </GrayBorderDiv>
            </BaseColSpace>
          </FlexRow>
        </FilterContext.Provider>
        <FlexRowWithTopMargin>
          <BaseCol>
            <TableWrapper
              tableInfo={pillarInfo}
              tableColumns={pillarColumns}
              header="Traffic Value by Content Pillar"
            />
          </BaseCol>
        </FlexRowWithTopMargin>
        <FlexRowWithTopMargin>
          <BaseCol>
            <TableWrapper
              tableInfo={audienceInfo}
              tableColumns={audienceColumns}
              header="Traffic Value by Audience"
            />
          </BaseCol>
        </FlexRowWithTopMargin>
        <FlexRowWithTopMargin>
          <BaseCol>
            <TableWrapper
              tableInfo={tagInfo}
              tableColumns={tagColumns}
              header="Traffic Value by Tag Group"
              tagFilter
              id="tag-table"
            />
          </BaseCol>
        </FlexRowWithTopMargin>
      </GridWithBottomMargin>
    </Fragment>
  );
}

export default ValueWrapper;

ValueWrapper.propTypes = {
  appData: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/value/value_wrapper.jsx